import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, XCircle, Clock, Pause, PencilSimple, ShieldSlash, Warning, Shield, Plus } from "@phosphor-icons/react";
import api from "@/lib/api";
import { HIGHLIGHT, focusFixTarget } from "@/components/pixels/pixelFix";
import { TrackingTestModal } from "@/components/tracking/TrackingTestModal";
import { TrackingHealthBadge } from "@/components/tracking/TrackingHealthBadge";

/**
 * Iter-56: Admin queue + review modal for Media Buyer custom tracking
 * requests. Gated by `landing_pages.approve_custom_tracking`.
 */

const STATUS_BADGE = {
  draft:    { label: "DRAFT",             cls: "bg-zinc-100 border-zinc-500 text-zinc-800", icon: PencilSimple },
  pending:  { label: "PENDING APPROVAL",  cls: "bg-amber-100 border-amber-600 text-amber-900", icon: Clock },
  approved: { label: "APPROVED",          cls: "bg-emerald-100 border-emerald-700 text-emerald-900", icon: CheckCircle },
  rejected: { label: "REJECTED",          cls: "bg-red-100 border-red-700 text-red-900", icon: XCircle },
  disabled: { label: "DISABLED",          cls: "bg-zinc-200 border-zinc-700 text-zinc-800", icon: Pause },
};


export default function AdminTrackingApprovals() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [killSwitch, setKillSwitch] = useState(false);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [reviewingId, setReviewingId] = useState(null);
  const [params] = useSearchParams();
  const fixKill = params.get("fix") === "kill";

  useEffect(() => {
    const rid = params.get("review");
    if (rid) setReviewingId(rid);
    if (params.get("fix") === "kill") focusFixTarget('[data-testid="admin-tracking-kill-switch"]');
  }, [params]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/custom-tracking?status=${statusFilter}`);
      setRows(r.data.requests || []);
      setCounts(r.data.counts || {});
      setKillSwitch(Boolean(r.data.kill_switch?.enabled));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load queue"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter]);

  const toggleKill = async () => {
    const next = !killSwitch;
    if (next && !window.confirm("Enable the GLOBAL kill switch? Every approved custom tracking snippet will stop firing platform-wide until this is cleared.")) return;
    try {
      await api.patch("/admin/custom-tracking/kill-switch", { enabled: next });
      toast.success(next ? "Kill switch ENABLED — all custom code is now inert." : "Kill switch cleared.");
      setKillSwitch(next);
    } catch (e) { toast.error(e.response?.data?.detail || "Toggle failed"); }
  };

  return (
    <div className="p-2" data-testid="admin-tracking-approvals">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <Shield size={22} weight="bold" />
        <h1 className="font-chivo font-black text-3xl tracking-tighter uppercase">
          Tracking Approvals
        </h1>
        {counts.pending > 0 && (
          <span
            className="bg-amber-500 text-black border-2 border-black px-2 py-0.5 text-xs font-black uppercase tracking-widest"
            data-testid="admin-tracking-pending-badge"
          >{counts.pending} pending</span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Link
            to="/media-buyer/custom-tracking"
            className="border-2 border-black bg-white px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white"
            data-testid="admin-tracking-new-request"
            title="Open the request form to submit your own custom tracking snippet"
          >
            <Plus size={14} className="inline mr-1" /> New request
          </Link>
          <button
            className={`border-2 px-3 py-1.5 text-[10px] uppercase tracking-widest ${killSwitch ? "border-red-700 bg-red-700 text-white" : "border-black hover:bg-black hover:text-white"} ${fixKill ? HIGHLIGHT : ""}`}
            onClick={toggleKill}
            data-testid="admin-tracking-kill-switch"
            title="Emergency stop: disables custom tracking for new visitors and terminates active sandbox trackers within approximately 30 seconds. Structured Meta / Google Ads / GA4 / TikTok pixels are NOT affected."
          >
            <ShieldSlash size={14} className="inline mr-1" />
            {killSwitch ? "Kill switch ON — click to release" : "Emergency kill switch"}
          </button>
        </div>
      </div>

      <div
        className="border-l-2 border-blue-700 bg-blue-50 pl-3 py-2 mb-3 text-sm"
        data-testid="admin-tracking-hint"
      >
        <b>How this works:</b> Media Buyers submit snippets from
        <Link to="/media-buyer/custom-tracking" className="underline mx-1 font-semibold">Custom Tracking</Link>
        → they appear here as <b>PENDING</b>. Approve (with one click) or reject with a note.
        Approved snippets run inside a sandboxed <code className="font-mono">&lt;iframe sandbox=&quot;allow-scripts&quot;&gt;</code> so
        they cannot read lead PII, cookies, or LeadFlow state. Need to submit your own snippet
        as an admin? Use the <b>New request</b> button above.
      </div>

      {killSwitch && (
        <div className="border-2 border-red-700 bg-red-50 p-3 mb-4 flex items-start gap-2" data-testid="admin-tracking-kill-banner">
          <Warning size={20} weight="fill" className="text-red-700 mt-0.5" />
          <div className="text-sm text-red-900">
            <b>Kill switch ACTIVE.</b> New visitors will not receive any custom tracking. Sandbox
            iframes on already-open tabs are terminated within approximately 30 seconds via the
            runtime&apos;s safety-state refresh (Iter-56.2). Structured Meta / Google Ads / GA4 /
            TikTok pixels are unaffected.
          </div>
        </div>
      )}

      {statusFilter === "pending" && counts.draft > 0 && (
        <div className="border-2 border-amber-600 bg-amber-50 p-3 mb-4 flex flex-wrap items-center gap-2" data-testid="admin-tracking-drafts-banner">
          <Warning size={18} weight="fill" className="text-amber-700" />
          <span className="text-sm text-amber-900 flex-1">
            {counts.draft} request{counts.draft === 1 ? " is" : "s are"} saved by a media buyer but not submitted yet. You can still review and approve {counts.draft === 1 ? "it" : "them"}.
          </span>
          <button type="button" onClick={() => setStatusFilter("draft")} data-testid="admin-tracking-drafts-open"
                  className="border-2 border-black bg-white px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white">
            Show not submitted
          </button>
        </div>
      )}

      <div className="mb-3 flex gap-2 flex-wrap" data-testid="admin-tracking-filters">
        {["pending", "draft", "approved", "rejected", "disabled", "all"].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            data-testid={`admin-tracking-filter-${s}`}
            className={`border border-black px-3 py-1.5 text-[10px] uppercase tracking-widest ${
              statusFilter === s ? "bg-black text-white" : "hover:bg-zinc-100"
            }`}
          >{s === "all" ? "All" : s === "draft" ? "Not submitted" : STATUS_BADGE[s]?.label} {counts[s] != null && <span className="opacity-70">({counts[s]})</span>}</button>
        ))}
      </div>

      {loading && <div className="label-mono">Loading…</div>}

      {!loading && rows.length === 0 && (
        <div className="brut-card p-6 text-center" data-testid="admin-tracking-empty">
          <div className="label-mono">No requests in this bucket</div>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <>
        <div className="hidden md:block brut-card overflow-x-auto">
        <table className="w-full border-collapse text-sm min-w-[960px]" data-testid="admin-tracking-table">
          <thead className="bg-zinc-100 border-b border-black">
            <tr className="text-left uppercase text-[10px] tracking-widest">
              <th className="py-2 px-3">Buyer</th>
              <th className="py-2 px-3">Provider</th>
              <th className="py-2 px-3">Name</th>
              <th className="py-2 px-3">Landing Page</th>
              <th className="py-2 px-3">Placement</th>
              <th className="py-2 px-3">Trigger</th>
              <th className="py-2 px-3">Submitted</th>
              <th className="py-2 px-3">Status</th>
              <th className="py-2 px-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const badge = STATUS_BADGE[r.status] || STATUS_BADGE.draft;
              const Icon = badge.icon;
              return (
                <tr key={r.id} className="border-t border-zinc-200" data-testid={`admin-tracking-row-${r.id}`}>
                  <td className="py-2 px-3">{r.media_buyer_name}</td>
                  <td className="py-2 px-3">{r.provider_name}</td>
                  <td className="py-2 px-3 font-semibold">{r.tracking_name}</td>
                  <td className="py-2 px-3 font-mono text-[11px]">{r.landing_page_slug}</td>
                  <td className="py-2 px-3 text-[11px] uppercase">{r.placement.replace("_"," ")}</td>
                  <td className="py-2 px-3 text-[11px] uppercase">{r.trigger.replace("_"," ")}</td>
                  <td className="py-2 px-3 text-[11px]">{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : "—"}</td>
                  <td className="py-2 px-3">
                    <span
                      className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] uppercase tracking-widest ${badge.cls}`}
                      data-testid={`admin-tracking-badge-${r.id}`}
                    >
                      <Icon size={11} weight="fill" />{badge.label}
                    </span>
                    <TrackingHealthBadge row={r} testId={`admin-tracking-health-${r.id}`} />
                  </td>
                  <td className="py-2 px-3">
                    <button
                      className="border-2 border-black bg-black text-white px-3 py-1 text-[10px] uppercase tracking-widest hover:bg-white hover:text-black"
                      onClick={() => setReviewingId(r.id)}
                      data-testid={`admin-tracking-review-${r.id}`}
                    >Review</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        {/* Mobile cards */}
        <div className="md:hidden flex flex-col gap-2" data-testid="admin-tracking-cards">
          {rows.map((r) => {
            const badge = STATUS_BADGE[r.status] || STATUS_BADGE.draft;
            const Icon = badge.icon;
            return (
              <div key={r.id} className="border border-black bg-white p-3" data-testid={`admin-tracking-card-${r.id}`}>
                <div className="flex items-center gap-2 mb-1 pb-1 border-b border-zinc-200">
                  <div className="font-bold text-sm truncate flex-1">{r.tracking_name}</div>
                  <span className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[9px] uppercase tracking-widest ${badge.cls}`}
                    data-testid={`admin-tracking-badge-mobile-${r.id}`}>
                    <Icon size={10} weight="fill" />{badge.label}
                  </span>
                </div>
                <TrackingHealthBadge row={r} testId={`admin-tracking-health-mobile-${r.id}`} />
                <div className="text-xs mt-1">{r.media_buyer_name} · {r.provider_name}</div>
                <div className="text-[11px] font-mono text-zinc-600 mt-0.5">/lp/{r.landing_page_slug}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">
                  {r.placement.replace("_"," ")} · {r.trigger.replace("_"," ")} · {r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : "—"}
                </div>
                <div className="mt-2">
                  <button className="border-2 border-black bg-black text-white px-3 py-1 text-[10px] uppercase tracking-widest"
                    onClick={() => setReviewingId(r.id)}
                    data-testid={`admin-tracking-review-mobile-${r.id}`}>Review</button>
                </div>
              </div>
            );
          })}
        </div>
        </>
      )}

      {reviewingId && (
        <AdminReviewModal
          id={reviewingId}
          onClose={() => setReviewingId(null)}
          onChanged={() => { setReviewingId(null); load(); }}
        />
      )}
    </div>
  );
}


function AdminReviewModal({ id, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/custom-tracking/${id}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
  }, [id]);

  const req = data?.request;
  const page = data?.landing_page;
  const s = data?.scan;

  const structuredPixels = useMemo(() => {
    const p = page?.pixel_overrides || {};
    const out = [];
    if (p.meta_pixel_id) out.push(`Meta: ${p.meta_pixel_id}`);
    if (p.google_ads_id) out.push(`Google Ads: ${p.google_ads_id}${p.google_ads_conversion_label ? " / " + p.google_ads_conversion_label : ""}`);
    if (p.ga4_measurement_id) out.push(`GA4: ${p.ga4_measurement_id}`);
    if (p.tiktok_pixel_id) out.push(`TikTok: ${p.tiktok_pixel_id}`);
    return out;
  }, [page]);

  const approve = async (enabled) => {
    setBusy(true);
    try {
      await api.post(`/admin/custom-tracking/${id}/approve`, { enabled });
      toast.success(enabled ? "Approved & enabled." : "Approved (disabled).");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    if (!rejectReason.trim()) return toast.error("Please add a reason so the Media Buyer knows what to change.");
    setBusy(true);
    try {
      await api.post(`/admin/custom-tracking/${id}/reject`, { review_note: rejectReason.trim() });
      toast.success("Rejected. The Media Buyer will see your note.");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    if (!window.confirm("Disable this approved snippet? It will stop firing immediately.")) return;
    setBusy(true);
    try {
      await api.post(`/admin/custom-tracking/${id}/disable`);
      toast.success("Disabled.");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Disable failed"); }
    finally { setBusy(false); }
  };

  const reenable = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/custom-tracking/${id}/enable`);
      toast.success("Re-enabled.");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Enable failed"); }
    finally { setBusy(false); }
  };

  const badge = req && (STATUS_BADGE[req.status] || STATUS_BADGE.draft);
  const verdict = s?.verdict || "pass";
  const verdictCls = {
    pass:    "bg-emerald-100 border-emerald-700 text-emerald-900",
    warning: "bg-amber-100 border-amber-700 text-amber-900",
    blocked: "bg-red-200 border-red-800 text-red-900",
  }[verdict] || "";

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto p-6"
         data-testid="admin-tracking-review-modal">
      <div className="bg-white border-2 border-black w-full max-w-4xl mt-8 mb-8">
        <div className="border-b border-black bg-black text-white px-5 py-3 flex items-center gap-3">
          <div className="font-chivo font-black text-xl uppercase truncate">
            {req?.tracking_name || "Loading…"}
          </div>
          {badge && <span className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] uppercase tracking-widest ${badge.cls}`}>{badge.label}</span>}
          <button className="ml-auto text-xs uppercase tracking-widest" onClick={onClose} data-testid="admin-tracking-review-close">Close</button>
        </div>
        {loading && <div className="p-6 label-mono">Loading…</div>}
        {req && !loading && (
          <div className="p-5 space-y-4">
            <div className="grid md:grid-cols-2 gap-x-6 gap-y-1">
              <MetaRow k="Media Buyer" v={req.media_buyer_name} />
              <MetaRow k="Landing page" v={`/lp/${req.landing_page_slug}`} />
              <MetaRow k="Provider" v={req.provider_name} />
              <MetaRow k="Placement" v={req.placement.replace("_"," ")} />
              <MetaRow k="Trigger" v={req.trigger.replace("_"," ")} />
              <MetaRow k="Version" v={`v${req.version}${req.parent_request_id ? " (new revision)" : ""}`} />
              <MetaRow k="Submitted" v={req.submitted_at ? new Date(req.submitted_at).toLocaleString() : "—"} />
              <MetaRow k="Code hash" v={(req.code_hash || "").slice(0, 16) + "…"} />
            </div>

            {req.notes_for_admin && (
              <div className="border-l-2 border-blue-700 bg-blue-50 pl-3 py-2 text-sm">
                <div className="label-mono">Notes from Media Buyer</div>
                <div>{req.notes_for_admin}</div>
              </div>
            )}

            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="label-mono">Code (escaped preview)</div>
                <button type="button" onClick={() => setTesting(true)} data-testid="admin-tracking-test"
                  className="ml-auto border border-emerald-800 text-emerald-900 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-emerald-800 hover:text-white">
                  Test this code
                </button>
              </div>
              <pre
                className="font-mono text-[11px] bg-zinc-900 text-emerald-100 border border-black p-3 max-h-80 overflow-auto whitespace-pre-wrap"
                data-testid="admin-tracking-review-code"
              >{req.code_snippet}</pre>
            </div>
            {testing && <TrackingTestModal snippet={req} onClose={() => setTesting(false)} />}

            <div className={`border-2 p-3 ${verdictCls}`} data-testid="admin-tracking-scan">
              <div className="flex items-center gap-2 mb-1">
                <ShieldSlash size={16} />
                <div className="uppercase font-black tracking-widest text-xs">
                  Static scan · {verdict === "pass" ? "no known high-risk patterns detected" : verdict}
                </div>
                <div className="ml-auto text-[11px]">{s?.size_bytes?.toLocaleString?.() || 0} bytes</div>
              </div>
              {s?.hard_blocks?.length > 0 && (
                <div className="text-sm"><b>Hard blocks:</b> {s.hard_blocks.join(", ")}</div>
              )}
              {s?.warnings?.length > 0 && (
                <div className="text-sm mt-1"><b>Warnings:</b> {s.warnings.join(", ")}</div>
              )}
              {s?.domains?.length > 0 && (
                <div className="text-sm mt-1"><b>External domains contacted:</b> {s.domains.join(", ")}</div>
              )}
              <div className="text-[11px] mt-2 italic opacity-80">
                Iter-56.1: static heuristic only — the real security boundary is the
                iframe sandbox. All approved custom tracking runs inside an
                <b> allow-scripts</b> iframe with a null opaque origin and cannot
                touch the parent form, cookies, or lead PII.
              </div>
            </div>

            <div
              className="border border-emerald-800 bg-emerald-50 text-emerald-900 p-3 text-sm"
              data-testid="admin-tracking-execution-mode"
            >
              <b>Execution:</b> SANDBOXED SCRIPT — runs inside <code className="font-mono">&lt;iframe sandbox=&quot;allow-scripts&quot;&gt;</code>.
              No access to LeadFlow DOM, cookies, storage, form inputs, or lead PII.
              Receives PII-safe context via one-way <code className="font-mono">postMessage</code>.
            </div>

            {structuredPixels.length > 0 && (
              <div className="border border-zinc-400 p-3 bg-zinc-50 text-sm">
                <div className="label-mono">Existing structured pixels on this page</div>
                <ul className="list-disc pl-4 mt-1">
                  {structuredPixels.map((s) => <li key={s} className="font-mono text-[11px]">{s}</li>)}
                </ul>
                <div className="text-[11px] mt-1 text-zinc-600">Check for duplicate conversion tracking before approving.</div>
              </div>
            )}

            {req.review_note && (
              <div className="border-l-2 border-red-700 bg-red-50 pl-3 py-2 text-sm">
                <div className="label-mono">Previous rejection note</div>
                <div>{req.review_note}</div>
              </div>
            )}
          </div>
        )}
        {req && !loading && (
          <div className="border-t border-black px-5 py-3 flex gap-2 flex-wrap items-center">
            {req.status === "draft" && (
              <div className="w-full text-xs text-amber-900 bg-amber-50 border border-amber-600 px-2 py-1" data-testid="admin-tracking-draft-note">
                The media buyer saved this but hasn&apos;t submitted it. You can still approve or reject it.
              </div>
            )}
            {(req.status === "pending" || req.status === "draft") && (
              <>
                <input
                  className="border border-black px-2 py-1.5 flex-1 min-w-[240px]"
                  placeholder="Rejection reason (required to reject)"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  data-testid="admin-tracking-reject-note"
                />
                <button
                  disabled={busy || !rejectReason.trim()}
                  onClick={reject}
                  className="border-2 border-red-700 text-red-700 px-4 py-2 text-[11px] uppercase tracking-widest hover:bg-red-700 hover:text-white disabled:opacity-40"
                  data-testid="admin-tracking-reject-btn"
                >Reject</button>
                <button
                  disabled={busy || verdict === "blocked"}
                  onClick={() => approve(false)}
                  className="border-2 border-black px-4 py-2 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white disabled:opacity-40"
                  data-testid="admin-tracking-approve-disabled"
                  title={verdict === "blocked" ? "Blocked by safety scan — ask the Media Buyer to revise or reject with a note." : "Approve but leave disabled until you're ready to flip it on."}
                >Approve (disabled)</button>
                <button
                  disabled={busy || verdict === "blocked"}
                  onClick={() => approve(true)}
                  className="border-2 border-emerald-800 bg-emerald-800 text-white px-4 py-2 text-[11px] uppercase tracking-widest hover:bg-emerald-900 disabled:opacity-40"
                  data-testid="admin-tracking-approve-enabled"
                  title={verdict === "blocked" ? "Blocked by safety scan — ask the Media Buyer to revise or reject with a note." : "One-click approve + enable on live pages."}
                >Approve & enable</button>
              </>
            )}
            {req.status === "approved" && req.enabled && (
              <button onClick={disable} disabled={busy}
                      className="border-2 border-red-700 text-red-700 px-4 py-2 text-[11px] uppercase tracking-widest hover:bg-red-700 hover:text-white ml-auto"
                      data-testid="admin-tracking-disable-btn">Disable snippet</button>
            )}
            {req.status === "approved" && !req.enabled && (
              <button onClick={reenable} disabled={busy}
                      className="border-2 border-emerald-800 bg-emerald-800 text-white px-4 py-2 text-[11px] uppercase tracking-widest hover:bg-emerald-900 ml-auto"
                      data-testid="admin-tracking-enable-btn">Re-enable snippet</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


function MetaRow({ k, v }) {
  return (
    <div className="flex gap-3 text-sm">
      <div className="label-mono w-32 shrink-0">{k}</div>
      <div className="font-mono text-[12px] break-all">{v}</div>
    </div>
  );
}
